import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '../core/components/Checkbox.tsx';
import FormField from '../core/components/FormField.tsx';
import { useGroupedFieldLabel, LINE_ITEM_PREFIX, GROUP_SEPARATOR } from './useFieldLabel.ts';

const LINE_ITEM_FIELD_PREFIX = `${LINE_ITEM_PREFIX}${GROUP_SEPARATOR}`;

export interface ChannelField {
    name: string;
    enabled: boolean;
    position: number | null;
}

const clusterByGroup = (fields: ChannelField[]): ChannelField[] => {
    const bases = fields.filter((f) => !f.name.includes(GROUP_SEPARATOR));
    const result: ChannelField[] = [];
    const claimed = new Set<string>();

    for (const base of bases) {
        result.push(base);
        claimed.add(base.name);
        for (const f of fields) {
            if (f.name.startsWith(`${base.name}${GROUP_SEPARATOR}`)) {
                result.push(f);
                claimed.add(f.name);
            }
        }
    }

    for (const f of fields) {
        if (!claimed.has(f.name)) result.push(f);
    }

    return result;
};

const FieldCheckboxGroup = ({
    title,
    fields,
    excludedFields,
    onToggle,
    labelFor,
}: {
    title: string;
    fields: ChannelField[];
    excludedFields: string[];
    onToggle: (name: string) => void;
    labelFor: (name: string) => string;
}) => {
    if (fields.length === 0) return null;
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-content-medium px-1.5 pb-0.5">{title}</span>
            {fields.map((f) => (
                <label
                    key={f.name}
                    className="flex gap-2 items-center hover:bg-surface-medium p-1.5 rounded cursor-pointer"
                >
                    <Checkbox checked={!excludedFields.includes(f.name)} onChange={() => onToggle(f.name)} />
                    <span className="text-sm text-content-strong">{labelFor(f.name)}</span>
                </label>
            ))}
        </div>
    );
};

interface ExcludedFieldsPickerProps {
    channelFields?: ChannelField[];
    excludedFields: string[];
    onToggle: (name: string) => void;
}

const ExcludedFieldsPicker = ({ channelFields, excludedFields, onToggle }: ExcludedFieldsPickerProps) => {
    const { t } = useTranslation('forms');
    const fieldLabel = useGroupedFieldLabel();

    const allEnabledFields = useMemo(() => (channelFields ?? []).filter((f) => f.enabled), [channelFields]);
    const headerFields = useMemo(
        () => clusterByGroup(allEnabledFields.filter((f) => !f.name.startsWith(LINE_ITEM_FIELD_PREFIX))),
        [allEnabledFields]
    );
    const lineItemFields = useMemo(
        () => clusterByGroup(allEnabledFields.filter((f) => f.name.startsWith(LINE_ITEM_FIELD_PREFIX))),
        [allEnabledFields]
    );

    if (allEnabledFields.length === 0) return null;

    return (
        <FormField label={t('tenant.shared.fields')} helpText={t('tenant.shared.fieldsHelp')}>
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto border border-solid border-line-weak rounded p-2">
                <FieldCheckboxGroup
                    title={t('tenant.shared.headerFields')}
                    fields={headerFields}
                    excludedFields={excludedFields}
                    onToggle={onToggle}
                    labelFor={fieldLabel}
                />
                <FieldCheckboxGroup
                    title={t('tenant.shared.lineItemFields')}
                    fields={lineItemFields}
                    excludedFields={excludedFields}
                    onToggle={onToggle}
                    labelFor={fieldLabel}
                />
            </div>
        </FormField>
    );
};

export default ExcludedFieldsPicker;
